import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import PersonIcon from "@mui/icons-material/Person";
import GroupsIcon from "@mui/icons-material/Groups";
import { RootState } from "../../store/store";
import { colors } from "../../data/colors";
import { stdObject } from "../../data/types/stdObject";
import { sendData } from "../../services/sendData";
import { useMainContext } from "../../context/MainContext";

const formatTime = (value: string) =>
  new Date(value).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

export default function NewChatPicker({
  onSelect,
}: {
  onSelect: (type: "user" | "role", code: number, name: string) => void;
}) {
  const { t } = useTranslation();
  const { logoutAction } = useMainContext();
  const user: any = useSelector((state: RootState) => state.data.user);
  const usersList: stdObject = useSelector(
    (state: RootState) => state.data.usersList,
  );
  const rol: stdObject[] = useSelector((state: RootState) => state.data.rol);

  const [tab, setTab] = useState<"users" | "roles">("users");
  const [filter, setFilter] = useState("");
  const [presenceByUser, setPresenceByUser] = useState<
    Record<number, { online: boolean; lastActive: string | null }>
  >({});

  useEffect(() => {
    sendData({ endPoint: "listUserPresence", logoutAction }).then((result) => {
      if (result.success && result.status) {
        const map: Record<
          number,
          { online: boolean; lastActive: string | null }
        > = {};
        (result.presence || []).forEach((p: stdObject) => {
          map[p.CODUSU] = { online: !!p.ONLUSU, lastActive: p.LASUSU ?? null };
        });
        setPresenceByUser(map);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const users = Object.entries(usersList || {})
    .map(([code, value]: [string, any]) => ({ ...value, CODUSU: Number(code) }))
    .filter(
      (u) =>
        u.CODUSU !== user.CODUSU &&
        u.ACTIVO === 1 &&
        !u.BLOUSU &&
        u.NOMUSU?.toLowerCase().includes(filter.toLowerCase()),
    )
    .map((u) => ({
      ...u,
      online: !!presenceByUser[u.CODUSU]?.online,
      lastActive: presenceByUser[u.CODUSU]?.lastActive ?? null,
    }))
    .sort((a, b) => {
      if (a.online !== b.online) return a.online ? -1 : 1;
      return (a.NOMUSU || "").localeCompare(b.NOMUSU || "");
    });

  const roles = (rol || []).filter((r) =>
    r.NOMROL?.toLowerCase().includes(filter.toLowerCase()),
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div
        style={{
          display: "flex",
          borderBottom: `1px solid ${colors.lightGrey2}`,
        }}
      >
        <button
          className="noSelect"
          onClick={() => setTab("users")}
          style={{
            flex: 1,
            padding: 10,
            border: "none",
            cursor: "pointer",
            backgroundColor:
              tab === "users" ? colors.white : colors.chatPanelBg,
            fontWeight: tab === "users" ? "bold" : "normal",
          }}
        >
          {t("p.chatUsersTab")}
        </button>
        <button
          className="noSelect"
          onClick={() => setTab("roles")}
          style={{
            flex: 1,
            padding: 10,
            border: "none",
            cursor: "pointer",
            backgroundColor:
              tab === "roles" ? colors.white : colors.chatPanelBg,
            fontWeight: tab === "roles" ? "bold" : "normal",
          }}
        >
          {t("p.chatRolesTab")}
        </button>
      </div>
      <div style={{ padding: 8 }}>
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t("p.chatSearchContacts")}
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: 6,
            borderRadius: 6,
            border: `1px solid ${colors.lightGrey2}`,
          }}
        />
      </div>
      <div style={{ flex: 1, overflowY: "auto" }}>
        {tab === "users"
          ? users.map((u) => (
              <div
                key={u.CODUSU}
                onClick={() => onSelect("user", u.CODUSU, u.NOMUSU)}
                className="noSelect"
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "8px 12px",
                  cursor: "pointer",
                  borderBottom: `1px solid ${colors.lightGrey}`,
                }}
              >
                <div
                  style={{
                    position: "relative",
                    display: "flex",
                    marginRight: 8,
                  }}
                >
                  <PersonIcon
                    style={{ color: u.online ? colors.green : colors.darkGrey }}
                  />
                </div>
                <div>
                  <div>{u.NOMUSU}</div>
                  <div style={{ fontSize: 11, color: colors.darkGrey }}>
                    {u.online
                      ? t("p.chatOnline")
                      : u.lastActive
                        ? `${t("p.chatLastSeen")} ${formatTime(u.lastActive)}`
                        : ""}
                  </div>
                </div>
              </div>
            ))
          : roles.map((r) => (
              <div
                key={r.CODROL}
                onClick={() => onSelect("role", r.CODROL, r.NOMROL)}
                className="noSelect"
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "8px 12px",
                  cursor: "pointer",
                  borderBottom: `1px solid ${colors.lightGrey}`,
                }}
              >
                <GroupsIcon
                  style={{ marginRight: 8, color: colors.darkGrey }}
                />
                {r.NOMROL}
              </div>
            ))}
      </div>
    </div>
  );
}
